import React, { useState, useEffect, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import MainLayout from './Layouts/MainLayout';
import eCoachVMSApi from './Api';
import Loading from './components/loading';
import Login from './pages/Login';

// Import NotFound component
const NotFound = lazy(() => import('./pages/NotFound'));

// Route handler that's aware of API loading state
const RouteHandler = ({ Component, pageName, isLoading, iframeUrl }) => {
  const [isComponentLoading, setIsComponentLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setIsComponentLoading(true);
    setHasError(false);

    if (!isLoading) {
      const loadComponent = async () => {
        try {
          await Component;
          setHasError(false);
        } catch (error) {
          setHasError(true);
        } finally {
          setIsComponentLoading(false);
        }
      };
      loadComponent();
    }
  }, [Component, isLoading]);

  if (isLoading || isComponentLoading) {
    return <Loading />;
  }

  if (hasError && !isLoading) {
    return (
      <React.Suspense fallback={<Loading />}>
        <NotFound />
      </React.Suspense>
    );
  }

  return (
    <React.Suspense fallback={<Loading />}>
      <Component pageName={pageName} iframeUrl={iframeUrl} />
    </React.Suspense>
  );
};

// Base components remain the same
const DefaultListView = ({ pageName, iframeUrl }) => {
  if (iframeUrl && (iframeUrl.startsWith("http://") || iframeUrl.startsWith("https://"))) {
    return (
      <div className='iframeview' style={{ width: '100%', height: '100vh' }}>
        <iframe 
          src={iframeUrl} 
          title={pageName}
          style={{ width: '100%', height: '100%', border: 'none' }}
        />
      </div>
    );
  }
  return <div>Default List View for {pageName}</div>;
};

const DefaultEditView = ({ pageName }) => {
  return <div>Default Edit View for {pageName}</div>;
};

const DefaultAddView = ({ pageName }) => {
  return <div>Default Add View for {pageName}</div>;
};

// loadComponent function remains the same
const loadComponent = (path) => {
  try {
    const segments = path.substring(1).split('/');
    const operation = segments[segments.length - 1];
    const isOperation = ['add', 'edit'].includes(operation.toLowerCase());
    const transformedSegments = segments.map((segment, index) => {
      if (index === segments.length - 1 && isOperation) {
        return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
      }
      return segment.split('-')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join('');
    });
    
    let componentPath;
    if (isOperation) {
      componentPath = transformedSegments.join('/');
    } else {
      componentPath = `${transformedSegments.join('/')}/index`;
    }
    //console.log('componentPath',componentPath);
    return lazy(() =>
      import(`./pages/${componentPath}`).catch((error) => {
      if (path.endsWith('/add')) {
        return Promise.resolve({ default: DefaultAddView });
      } else if (path.includes('/edit')) {
        return Promise.resolve({ default: DefaultEditView });
      }
      return Promise.resolve({ default: DefaultListView });
    })
  );
} catch (error) {
  return DefaultListView;
}
};

const AppRoutes = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [componentMap, setComponentMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const { baseURL } = eCoachVMSApi();
  
  // Build menu tree function remains the same
  const buildMenuTree = (items) => {
    const itemMap = new Map();
    const menuTree = [];
    
    items.forEach(item => {
      itemMap.set(item.id, { ...item, children: [] });
    });
    
    items.forEach(item => {
      if (item.parent_id === null) {
        menuTree.push(itemMap.get(item.id));
      } else {
        const parent = itemMap.get(item.parent_id);
        if (parent) {
          parent.children.push(itemMap.get(item.id));
        }
      }
    });
    
    return menuTree;
  };
  
  // CRUD routes generation remains the same
  const generateCrudRoutes = (path) => {
    const basePath = path.endsWith('/') ? path.slice(0, -1) : path;
    return [
      { 
        path: `${basePath}/add`,
        component: loadComponent(`${basePath}/add`)
      },
      { 
        path: `${basePath}/edit/:id`,
        component: loadComponent(`${basePath}/edit`)
      }
    ];
  };
  
  // Component map building remains the same
  const buildComponentMap = (items) => {
    const map = {};
    const crudPaths = [
      '/tools',
      '/service-tools',
      '/work-tickets',
      '/daily-service',
      '/settings',
      '/unscheduled',
      '/service-calendar'
    ];
    
    const processItem = (item) => {
      if (item.is_active !== 1) return;
      const normalizedPath = item.react_path.toLowerCase();
      map[normalizedPath] = loadComponent(normalizedPath);
      
      const shouldHaveCrud = crudPaths.some(crudPath => 
        normalizedPath === crudPath || normalizedPath.startsWith(`${crudPath}/`)
      );
      
      if (shouldHaveCrud) {
        const crudRoutes = generateCrudRoutes(normalizedPath);
        crudRoutes.forEach(route => {
          map[route.path] = route.component;
        });
      }
      
      if (item.children?.length > 0) {
        item.children.forEach(processItem);
      }
    };
    
    items.forEach(processItem);
    return map;
  };
  
  // Modified useEffect to use isLoading state
  useEffect(() => {
    const fetchMenu = async () => {
      setIsLoading(true);
      try {
        const response = await baseURL.get('/getMenu');
        const data = await response.data;
        //console.log('data',data)
        const menuTree = buildMenuTree(data);
        setMenuItems(menuTree);
        
        const dynamicComponentMap = buildComponentMap(data);
        setComponentMap(dynamicComponentMap);
      } catch (error) {
        console.error('Error fetching menu:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchMenu();
    // eslint-disable-next-line
  }, []);
  
  // Menu items flattening remains the same
  const flattenMenuItems = (items) => {
    return items.reduce((acc, item) => {
      acc.push(item);
      if (item.children?.length > 0) {
        acc.push(...flattenMenuItems(item.children));
      }
      return acc;
    }, []);
  };
  
  // Modified to use isLoading
  const generateDynamicRoutes = () => {
    const flatRoutes = flattenMenuItems(menuItems);
    
    return flatRoutes.map(route => {
      const Component = componentMap[route.react_path.toLowerCase()];
      
      if (Component) {
        return (
          <Route 
          key={route.react_path} 
          path={route.react_path}
          element={
            <RouteHandler 
            Component={Component} 
            pageName={route.name}
            isLoading={isLoading}
            iframeUrl={route.tooltip} // Pass the iframe_url here
            />
          }
          />
        );
      }
      return null;
    }).filter(Boolean);
  };
  
  // Modified to use isLoading
  const generateCrudRoutesFromMenu = () => {
    const flatRoutes = flattenMenuItems(menuItems);
    const crudPaths = [
      '/tools',
      '/service-tools',
      '/work-tickets',
      '/daily-service',
      '/settings',
      '/unscheduled',
      '/service-calendar'
    ];
    

    const getEditRoute = (basePath, route, EditComponent) => {
      // Handle special cases for edit routes
      if (basePath === '/settings/system-settings') {
        return {
          key: `${basePath}/edit/:id`,
          path: `${basePath}/edit/:id`,
          paramName: 'id'
        };
      } else if (basePath === '/settings/dropdown') {
        return {
          key: `${basePath}/edit`,
          path: `${basePath}/edit`,
          paramName: 'typeName'
        };
      } else {
        return {
          key: `${basePath}/edit/:id`,
          path: `${basePath}/edit/:id`,
          paramName: 'id'
        };
      }
    };

    return flatRoutes.flatMap(route => {
      if (route.is_active !== 1) return [];
      
      const basePath = route.react_path.toLowerCase();
      const shouldHaveCrud = crudPaths.some(crudPath => 
        basePath === crudPath || basePath.startsWith(`${crudPath}/`)
      );
      if (shouldHaveCrud) {
        const AddComponent = loadComponent(`${basePath}/add`);
        const EditComponent = loadComponent(`${basePath}/edit`);
        
        // Create add route
        const addRoute = (
          <Route 
            key={`${basePath}/add`}
            path={`${basePath}/add`}
            element={
              <RouteHandler 
                Component={AddComponent} 
                pageName={`Add ${route.name}`}
                iframeUrl={route.tooltip} // Pass the iframe_url here
                isLoading={isLoading}
              />
            }
          />
        );
  
        // Get edit route configuration
        const editRouteConfig = getEditRoute(basePath, route, EditComponent);
        
        // Create edit route
        const editRoute = (
          <Route 
            key={editRouteConfig.key}
            path={editRouteConfig.path}
            element={
              <RouteHandler 
                Component={EditComponent} 
                pageName={`Edit ${route.name}`}
                isLoading={isLoading}
              />
            }
          />
        );
  
        return [addRoute, editRoute];
      }
      return [];
    });
  };
  const ServiceCalendarComponent = componentMap['/service-calendar'];
  return (
    
    <Routes>
      <Route path='/login' component={Login} />
      <Route path="/" element={<MainLayout />}>
        <Route 
            index 
            element={
              <RouteHandler 
                Component={ServiceCalendarComponent || loadComponent('/service-calendar')} 
                pageName="Service Calendar"
                isLoading={isLoading}
              />
            }
          />
      {generateDynamicRoutes()}
      {generateCrudRoutesFromMenu()}
        <Route
        path="*"
        element={
          isLoading ? (
            <Loading />
          ) : (
            <React.Suspense fallback={<Loading />}>
            <NotFound />
            </React.Suspense>
          )
        }
        />
      </Route>
    </Routes>
  );
};

export default AppRoutes;